import React from 'react';
import { useLocation } from 'react-router-dom';
import NotificationsBell from './NotificationsBell';
import MessagesMenu from './MessagesMenu';
import { usePortalBrandContext } from '../../hooks/usePortalBrandContext';
import { TOPBAR_AGENT_SLOT_ID, TOPBAR_SLOT_ID } from '../v2/topBarSlots';
import { shellPath, v2PageName } from './v2Shell';

/**
 * The v2 top bar — the mock's `.topbar` (built-v12): 52px white bar,
 * "Brand · Page" on the left, then the page's tabs + "Data through" stamp
 * (portaled into #topbar-slot by components/v2/TopBar), "Ask the agent"
 * (portaled into #topbar-agent-slot by AdvisorDock), and the bell. The menu
 * button opens the sidebar drawer at 860px and below. At 680px and below the
 * breadcrumb gives way to the tabs, as in the mock.
 */
const NavbarV2: React.FC<{ toggleSidebar: () => void }> = ({ toggleSidebar }) => {
  const path = shellPath(useLocation().pathname);
  const { brandName } = usePortalBrandContext();
  const page = v2PageName(path);

  return (
    <header className="group/top z-10 flex h-[52px] leading-[1.45] antialiased flex-none items-center gap-2 border-b border-line bg-white px-4 text-[13px] text-ink-muted min-[861px]:px-8">
      <button
        type="button"
        aria-label="Menu"
        onClick={toggleSidebar}
        className="grid h-[34px] w-[34px] flex-none place-items-center rounded-lg border border-line min-[861px]:hidden"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[18px] w-[18px] fill-none stroke-ink" strokeWidth={1.8} strokeLinecap="round">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>

      <div className="flex min-w-0 items-center gap-2 whitespace-nowrap max-[680px]:group-has-[#topbar-slot>*]/top:hidden">
        {brandName && <span className="truncate">{brandName}</span>}
        {brandName && page && <span aria-hidden="true">·</span>}
        {page && <b className="font-semibold text-ink">{page}</b>}
      </div>

      {/* Right cluster: page tabs + freshness stamp (v2 pages only; empty
          elsewhere), the agent, Global Bridge notices, the bell. */}
      <div className="ml-auto flex min-w-0 items-center">
        <div id={TOPBAR_SLOT_ID} className="flex min-w-0 items-center empty:hidden" />
        <div id={TOPBAR_AGENT_SLOT_ID} className="ml-[18px] flex flex-none items-center empty:hidden" />
        {/* Notices from Global Bridge (rate card): a live v1 feature the mock
            does not draw; it hides itself when nothing applies. */}
        <div className="ml-[18px] flex-none empty:hidden">
          <MessagesMenu />
        </div>
        <div className="ml-[18px] flex-none">
          <NotificationsBell />
        </div>
      </div>
    </header>
  );
};

export default NavbarV2;
